import { Component, Inject, Input, NgZone, OnDestroy, OnInit, afterNextRender } from '@angular/core';

import { AuthService } from '@shared/modules/auth/services/auth.service';
import { ConfigurationService } from '@shared/services/configuration.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { StudentProfileDataService } from '../../../student-profile/services/student-profile-data.service';
import { WINDOW } from '@shared/services/window.service';

import { environment } from 'environments/environment';

// Icons
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

export interface ContestInfo {
  CompetitionId: number;
  EndUTC: Date;
  IsFree: boolean;
  IsRegistered: boolean;
  Price: number;
  SessionId: string;
  StartUTC: Date;
}

@Component({
  selector: 'home-action-button',
  templateUrl: './action-button.component.html',
  styleUrl: './action-button.component.scss',
  standalone: false
})
export class ActionButtonComponent implements OnDestroy, OnInit {

  @Input() buttonClass: string = '';
  @Input() contest: ContestInfo | null = null;

  private storageKeyClient = 'CLIENT';

  // Configuration
  cfgGracePeriodMinutes: number = 10;
  keyGracePeriodMinutes = 'CONTEST-REGISTRATION-GRACE-PERIOD-MINUTES';

  // Icons
  faSpinner = faSpinner;

  // Model
  environment = environment;
  intervalUpdateCurrentStatus: any | null = null;
  isClosed = false;
  isComing = false;
  isOver = false;
  isRunning = false;
  isUpcoming = false;
  isStudent = false;
  status = '';
  studentId: number = null;

  // Progress
  errorRegistering: any = null;
  isRegistering = false;

  constructor(
    private authService: AuthService,
    private configurationService: ConfigurationService,
    private localStorageService: LocalStorageService,
    private ngZone: NgZone,
    private studentProfileDataService: StudentProfileDataService,
    @Inject(WINDOW) private window: Window | null
  ) {
    afterNextRender({
      'write': () => {
        if (!this.intervalUpdateCurrentStatus) {
          setTimeout(() => this.updateCurrentStatus(), 0);
          this.intervalUpdateCurrentStatus = setInterval(() => this.updateCurrentStatus(), 1000);
        }
      }
    })
  }

  ngOnDestroy() {

    // Cancel the regular current contest status update
    if (this.intervalUpdateCurrentStatus) {
      clearInterval(this.intervalUpdateCurrentStatus);
      this.intervalUpdateCurrentStatus = null;
    }
  }

  ngOnInit() {

    // Read configuration
    this.configurationService.getItem(this.keyGracePeriodMinutes)
      .subscribe((value) => this.cfgGracePeriodMinutes = +value.Value);

    // Check whether student is signed in
    this.isStudent = this.authService.isAuthorized(['STUDENT']);
    if (this.isStudent) {
      this.studentId = this.authService.getUserId();
    }
  }

  goCheckout() {
    if (this.window) {
      this.window.location.href = '/checkout?s=' + this.contest.SessionId + '&c=USD';
    }
  }

  goRegister() {
    this.register();
  }

  private register() {

    if (this.isRegistering) {
      return;
    }

    // Show spinner
    this.isRegistering = true;

    // Reset error status
    this.errorRegistering = null;

    const payload = {
      ClientId: this.localStorageService.getItem(this.storageKeyClient),
      HTTPRequest: this.window && this.window.location.href.replace(this.window.location.origin, '')
    }

    this.studentProfileDataService.createRegistration(this.studentId, this.contest.CompetitionId, payload)
      .subscribe({
        next: (value) => {
          this.contest.IsRegistered = true;
          this.isRegistering = false;
          this.ngZone.run(() => this.setStatus());
        },
        error: (error) => {
          this.errorRegistering = error;
          this.isRegistering = false;
        }
      });
  }

  private setStatus() {

    if (this.isStudent) {

      // Participant
      if (this.contest.IsRegistered && this.isComing) {
        this.status = 'REGISTERED-COMING';
      } else if (this.contest.IsRegistered && this.isRunning) {
        this.status = 'REGISTERED-RUNNING';
      } else if (this.contest.IsRegistered && this.isOver) {
        this.status = 'REGISTERED-OVER';
      } else if (this.contest.IsRegistered) {
        this.status = this.isUpcoming ? 'REGISTERED' : 'PAST';
      } else if (!this.contest.IsRegistered && (this.isComing || this.isUpcoming)) {
        this.status = this.isClosed ? 'IN-PROGRESS' : (this.contest.IsFree ? 'REGISTER-FREE' : 'REGISTER-CUSTOMER');
      } else /*if (!this.contest.IsRegistered)*/ {
        this.status = 'PAST';
      }
    } else {

      // Customer
      if (this.isComing || this.isUpcoming) {
        this.status = this.isClosed ? 'IN-PROGRESS' : 'REGISTER';
      } else {
        this.status = 'PAST';
      }
    }
  }

  private updateCurrentStatus() {

    if (this.contest) {
      const now = new Date();
      this.isClosed = now > new Date(this.contest.StartUTC.getTime() + (this.cfgGracePeriodMinutes * 60 * 1000));
      this.isComing = now < this.contest.StartUTC;
      this.isOver = now > this.contest.EndUTC;
      this.isRunning = !this.isOver && !this.isComing;
      this.isUpcoming = !this.isOver;

      // Update status
      this.ngZone.run(() => this.setStatus());

      // Cancel the regular current contest status update if it is past
      if (this.intervalUpdateCurrentStatus && (this.status === 'PAST')) {
        clearInterval(this.intervalUpdateCurrentStatus);
        this.intervalUpdateCurrentStatus = null;
      }
    }
  }
}
